import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Trash2, Loader2, UserCog, Shield, Users as UsersIcon, Scale } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Users = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    name: '',
    role: 'employee',
    department: '',
    openclaw_access: false
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.name) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/users`, newUser);
      setUsers([...users, response.data]);
      setNewUser({ email: '', password: '', name: '', role: 'employee', department: '', openclaw_access: false });
      setDialogOpen(false);
      toast.success('Benutzer erstellt');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Benutzer wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`);
      setUsers(users.filter(u => u.id !== userId));
      toast.success('Benutzer gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleToggleOpenClaw = async (userId) => {
    try {
      const response = await axios.put(`${API}/users/${userId}/openclaw-access`);
      setUsers(users.map(u => 
        u.id === userId ? { ...u, openclaw_access: response.data.openclaw_access } : u
      ));
      toast.success(response.data.openclaw_access ? 'OpenClaw-Zugriff aktiviert' : 'OpenClaw-Zugriff deaktiviert');
    } catch (error) {
      toast.error('Fehler beim Ändern der Berechtigung');
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <Badge className="bg-red-100 text-red-700"><Shield className="h-3 w-3 mr-1" />{t('admin')}</Badge>;
      case 'manager':
        return <Badge className="bg-blue-100 text-blue-700"><UserCog className="h-3 w-3 mr-1" />{t('manager')}</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-700"><UsersIcon className="h-3 w-3 mr-1" />{t('employee')}</Badge>;
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 font-[Manrope]">{t('users')}</h2>
          <p className="text-slate-500 mt-1 text-sm">{users.length} Benutzer verwalten</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#D92632] hover:bg-[#B91C26] text-white h-9 sm:h-10 text-sm"
              data-testid="new-user-btn"
            >
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Neuer Benutzer</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto mx-4">
            <DialogHeader>
              <DialogTitle className="font-[Manrope]">Neuer Benutzer</DialogTitle>
              <DialogDescription>Erstellen Sie einen neuen Benutzeraccount</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  placeholder="Max Mustermann"
                  className="mt-1"
                  data-testid="new-user-name"
                />
              </div>
              
              <div>
                <Label>{t('email')} *</Label>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="email@kaufmanns-consulting.com"
                  className="mt-1"
                  data-testid="new-user-email"
                />
              </div>
              
              <div>
                <Label>{t('password')} *</Label>
                <Input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="••••••••"
                  className="mt-1"
                  data-testid="new-user-password"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('role')}</Label>
                  <Select 
                    value={newUser.role} 
                    onValueChange={(v) => setNewUser({ ...newUser, role: v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">{t('admin')}</SelectItem>
                      <SelectItem value="manager">{t('manager')}</SelectItem>
                      <SelectItem value="employee">{t('employee')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Abteilung</Label>
                  <Input
                    value={newUser.department}
                    onChange={(e) => setNewUser({ ...newUser, department: e.target.value })}
                    placeholder="z.B. Marketing"
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  {t('cancel')}
                </Button>
                <Button 
                  onClick={handleCreateUser}
                  className="bg-[#D92632] hover:bg-[#B91C26]"
                  disabled={saving}
                  data-testid="save-user-btn"
                >
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Erstellen
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users Table - Desktop */}
      <Card className="bg-white border border-slate-200 hidden sm:block">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Benutzer</TableHead>
                  <TableHead>{t('role')}</TableHead>
                  <TableHead>Abteilung</TableHead>
                  <TableHead>OpenClaw</TableHead>
                  <TableHead>Erstellt am</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#D92632]/10 flex items-center justify-center text-[#D92632] font-semibold">
                          {user.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{user.name}</p>
                          <p className="text-sm text-slate-500">{user.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    <TableCell className="text-slate-600">{user.department || '-'}</TableCell>
                    <TableCell>
                      {user.role === 'admin' ? (
                        <Badge className="bg-cyan-100 text-cyan-700">
                          <Scale className="h-3 w-3 mr-1" />
                          Admin
                        </Badge>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleOpenClaw(user.id)}
                          className={user.openclaw_access ? 'text-cyan-600 hover:text-cyan-700' : 'text-slate-400 hover:text-slate-600'}
                          data-testid={`toggle-openclaw-${user.id}`}
                        >
                          <Scale className="h-4 w-4 mr-1" />
                          {user.openclaw_access ? 'Aktiv' : 'Inaktiv'}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-500">
                      {format(new Date(user.created_at), 'dd.MM.yyyy')}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-slate-400 hover:text-red-500"
                        onClick={() => handleDeleteUser(user.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Users Cards - Mobile */}
      <div className="sm:hidden space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : (
          users.map((user) => (
            <Card key={user.id} className="bg-white border border-slate-200" data-testid={`user-card-${user.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#D92632]/10 flex items-center justify-center text-[#D92632] font-semibold">
                      {user.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{user.name}</p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-slate-400 hover:text-red-500 h-8 w-8"
                    onClick={() => handleDeleteUser(user.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  {getRoleBadge(user.role)}
                  {user.department && (
                    <Badge variant="outline" className="text-xs">{user.department}</Badge>
                  )}
                  {user.role !== 'admin' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleOpenClaw(user.id)}
                      className={`h-6 text-xs ${user.openclaw_access ? 'text-cyan-600' : 'text-slate-400'}`}
                    >
                      <Scale className="h-3 w-3 mr-1" />
                      OpenClaw: {user.openclaw_access ? 'Aktiv' : 'Inaktiv'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default Users;
